import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import saveAs from 'file-saver';
import { Download, Eye, Trash2 } from 'lucide-react';

import ViewReportDialog from 'components/dialogs/ViewReportDialog';
import SocialShare from 'components/SocialShare';
import ReportActionButton from 'components/ReportActionButton';
import { useDeleteVoiceReport } from 'services/voice-scan/hooks';
import { captureUserActivity } from 'services/profile/api';
import { USER_ACTIVITY } from 'types';
import { VoiceReportDetail } from 'services/voice-scan/type';

interface ReportActionsProps {
  scanData?: VoiceReportDetail;
  readingId: string;
  refetchScanReports?: () => void;
}

export default function ReportActions({
  scanData,
  readingId,
  refetchScanReports,
}: ReportActionsProps) {
  const { t } = useTranslation();

  const [openView, setOpenView] = useState<boolean>(false);

  const { mutate: onDelete, isPending: isDeleting } = useDeleteVoiceReport({
    onSuccess: () => {
      captureUserActivity(USER_ACTIVITY.DELETE_VOICE_SCAN_REPORT, {
        session_ids: [readingId],
      });
      refetchScanReports?.();
    },
  });

  const shareableReport = useCallback(() => {
    if (!scanData) return '';

    captureUserActivity(USER_ACTIVITY.VOICE_SCAN_REPORT_SHARE);
    let message = `Wellness Score: ${scanData.wellness_score}\n\n`;
    scanData?.voice_scan_report.forEach((item) => {
      message += `${item.key}: ${item.value}`;
      if (item.category) {
        message += ` (${item.category})`;
      }
      message += '\n';
    });
    return message;
  }, [scanData]);

  const scanReport = shareableReport();

  const onDownload = () => {
    const blob = new Blob([scanReport], { type: 'text/plain;charset=utf-8' });
    saveAs(blob, 'voice_report.txt');
  };

  return (
    <>
      <div className='flex items-center justify-center mt-2 mb-4 sm:mt-0 sm:justify-end gap-x-2'>
        <SocialShare content={shareableReport()} isLoading={false} />
        <ReportActionButton
          icon={Eye}
          text={t('view')}
          onClick={() => setOpenView(true)}
        />
        <ReportActionButton
          icon={Download}
          text={t('download')}
          onClick={() => onDownload()}
        />
        <ReportActionButton
          icon={Trash2}
          text={t('delete')}
          onClick={() => onDelete({ session_id: [readingId] })}
          isLoading={isDeleting}
          className='text-red-500 border-red-500 hover:bg-red-500/10 hover:text-red-500'
        />
      </div>

      <ViewReportDialog
        open={openView}
        onClose={() => setOpenView(false)}
        report={scanReport}
        contentClassName='max-w-lg'
      />
    </>
  );
}
